import React from 'react';
import PropTypes from 'prop-types';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const chartContainerStyle = {
  width: '100%',
  height: 300,
  margin: '10px',
  background: '#fff',
  borderRadius: 8,
  padding: '10px 0 80px 0',
  transform: 'rotate(180deg)',
};

const chartTitleStyle = {
  margin: '0 0 16px',
  fontSize: 16,
  color: '#333',
};

const chartFooterStyle = {
  margin: '16px 0 0',
  fontSize: 12,
  color: '#666',
};

const tooltipStyle = {
  backgroundColor: 'white',
  padding: '10px',
  border: '1px solid #ccc',
  borderRadius: '4px',
};

const tooltipLabelStyle = {
  margin: '0 0 5px',
};

const tooltipValueStyle = {
  margin: '0',
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={tooltipStyle}>
        <p style={tooltipLabelStyle}>{label}</p>
        {payload.map(entry => (
          <p
            key={entry.name}
            style={{ ...tooltipValueStyle, color: entry.color }}
          >
            {entry.name}
            {': '}
            {entry.value.toFixed(2)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.number,
      color: PropTypes.string,
    }),
  ),
  label: PropTypes.string,
};

CustomTooltip.defaultProps = {
  active: false,
  payload: [],
  label: '',
};

const BarChartMessage = ({ data }) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return null;
  }

  const { data: chartData, config, chartConfig } = data[0].data;
  const fallbackGray = ['#444', '#666', '#888', '#aaa', '#ccc'];

  return (
    <div style={chartContainerStyle}>
      {config.title && <h3 style={chartTitleStyle}>{config.title}</h3>}
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          margin={{
            top: 5,
            right: 30,
            left: 20,
            bottom: 50,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey={config.xAxisKey}
            angle={90}
            textAnchor="end"
            height={60}
            interval={0}
            tick={{ fontSize: 14 }}
            dy={5}
          />
          <YAxis
            domain={['dataMin - 1', 'dataMax + 1']}
            tickFormatter={value => `${value}`}
            dx={-35}
          />
          <Tooltip content={<CustomTooltip />} />
          {Object.entries(chartConfig).map(([key, value], idx) => (
            <Bar
              key={key}
              dataKey={key}
              fill={value.color || fallbackGray[idx % fallbackGray.length]}
              name={value.label}
              radius={[4, 4, 0, 0]}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
      {config.footer && <p style={chartFooterStyle}>{config.footer}</p>}
    </div>
  );
};

BarChartMessage.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      data: PropTypes.shape({
        data: PropTypes.arrayOf(
          PropTypes.object.isRequired,
        ).isRequired,
        config: PropTypes.shape({
          title: PropTypes.string,
          footer: PropTypes.string,
          xAxisKey: PropTypes.string.isRequired,
          description: PropTypes.string,
        }).isRequired,
        chartType: PropTypes.string.isRequired,
        chartConfig: PropTypes.objectOf(
          PropTypes.shape({
            color: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
          }),
        ).isRequired,
      }).isRequired,
    }),
  ),
};

BarChartMessage.defaultProps = {
  data: [],
};

export default BarChartMessage;
